"use client";

import { useEffect, useMemo, useRef } from "react";
import Link from "next/link";
import { HeroCard } from "@/components/ui/molecules/hero-card";
import { track } from "@/lib/analytics";
import { listPrograms, type ProgramListItem } from "@/lib/api/api-client";
import { useAuthedView } from "@/lib/learn/use-authed-view";
import { learnProgramPath } from "@/lib/learn/learn-routes";
import { useUnlockedPrograms } from "@/lib/learning-journey/use-program-progress";
import { getLevelStyle } from "./level-styles";

/**
 * Order the programs so the prerequisites of each come before it.
 *
 * The API sorts by level alone, which says nothing: two programs can share a
 * level and still have one strictly before the other. Without this, a beginner
 * sees interchangeable cards that are not interchangeable, and every step that
 * turns out to be too high reads as a personal failure rather than a wrong turn.
 */
function inPrerequisiteOrder(programs: ProgramListItem[]): ProgramListItem[] {
  const byVpfId = new Map(programs.map((p) => [p.vpfId, p]));
  const placed = new Set<string>();
  const ordered: ProgramListItem[] = [];

  const place = (program: ProgramListItem, seen: Set<string>) => {
    if (placed.has(program.vpfId) || seen.has(program.vpfId)) {
      return;
    }

    seen.add(program.vpfId);

    for (const required of program.prerequisites) {
      const prerequisite = byVpfId.get(required);

      if (prerequisite) {
        place(prerequisite, seen);
      }
    }

    placed.add(program.vpfId);
    ordered.push(program);
  };

  const byLevel = [...programs].sort((a, b) => a.level - b.level);

  for (const program of byLevel) {
    place(program, new Set());
  }

  return ordered;
}

/**
 * Les parcours viennent du serveur, déjà rendus dans le HTML. Ce composant
 * reste client uniquement pour l'ordre et le verrouillage, qui dépendent de la
 * progression rangée dans le navigateur.
 */
export function ProgramListPage({
  initialPrograms,
}: {
  initialPrograms: ProgramListItem[];
}) {
  // Idem : le HTML servi est celui d'un visiteur anonyme, donc tout y est
  // fermé sauf le parcours d'entrée. Un compte rouvre ce qu'il ouvre.
  const { view: programs, settled } = useAuthedView(
    initialPrograms,
    async () => (await listPrograms(1, 50)).items,
  );

  const ordered = useMemo(() => inPrerequisiteOrder(programs), [programs]);
  const unlocked = useUnlockedPrograms(ordered);
  const titleByVpfId = useMemo(
    () => new Map(programs.map((p) => [p.vpfId, p.title])),
    [programs],
  );

  /*
    L'échelle telle qu'elle apparaît, une fois seulement.

    Deux attentes, et aucune n'est optionnelle. `settled` écarte la vue
    anonyme rendue par le serveur : émettre avant compterait tous les parcours
    d'une personne connectée comme fermés. `unlocked` non nul écarte l'état
    d'avant lecture du stockage, où aucun prérequis n'est opposé — voulu à
    l'écran, mais ça compterait tout comme jouable.

    Les deux mentent en sens contraire, ce qui ne les annule pas : ça donnerait
    une échelle à la fois toute fermée et toute ouverte.
  */
  const listReported = useRef(false);

  useEffect(() => {
    if (!settled || unlocked === null || ordered.length === 0 || listReported.current) {
      return;
    }

    listReported.current = true;

    track({
      name: "program_list_opened",
      properties: {
        programCount: ordered.length,
        unlockedCount: ordered.filter((p) => !p.locked && unlocked.has(p.vpfId)).length,
        lockedCount: ordered.filter((p) => p.locked).length,
      },
    });
  }, [ordered, settled, unlocked]);

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 px-6 py-10">
      <div className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">
          Apprendre
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          Sept parcours, dans cet ordre. Chacun se termine sur un morceau que tu
          joues en entier.
        </p>
      </div>

      {ordered.length === 0 && (
        <p className="py-10 text-center text-sm text-text-muted">
          Aucun parcours disponible.
        </p>
      )}

      {ordered.map((program, index) => {
        const style = getLevelStyle(program.level);
        const after = program.prerequisites
          .map((id) => titleByVpfId.get(id))
          .filter((title): title is string => Boolean(title));

        const meta = [
          `${program.pieceCount} morceaux`,
          `${program.exerciseCount} exercices`,
          `${program.session.minutes} min par jour`,
        ];

        /*
          Deux verrous, et il ne faut pas les confondre à l'écran.

          L'accès dit qui a le droit d'ouvrir le contenu ; les prérequis disent
          si c'est le bon moment. Le premier se lève en créant un compte, le
          second en jouant. Les afficher pareil laisserait croire qu'un compte
          débloque un parcours qu'on n'a simplement pas encore mérité.
        */
        // Tant que le stockage n'a pas été lu, aucun prérequis n'est opposé :
        // mieux vaut ouvrir une carte une frame de trop que poser un verrou
        // puis le retirer sous les yeux de quelqu'un.
        const needsPrerequisite = unlocked !== null && !unlocked.has(program.vpfId);
        const dimmed = program.locked || needsPrerequisite;

        const note = program.locked
          ? program.access === "paid"
            ? "Bloqué pour le moment"
            : "Crée un compte pour ouvrir ce parcours"
          : after.length > 0
            ? `${needsPrerequisite ? "Se débloque après" : "Après"} ${after.join(" et ")}`
            : null;

        const label = program.locked
          ? program.access === "paid"
            ? "Bloqué pour le moment"
            : "Voir ce parcours"
          : needsPrerequisite
            ? "Regarder quand même"
            : "Commencer le parcours";

        return (
          <div key={program.id} className="flex flex-col gap-2">
            {note !== null && (
              <p className="pl-1 text-[12px] text-text-muted">{note}</p>
            )}

            <Link
              href={learnProgramPath(program.vpfId)}
              className={dimmed ? "opacity-45 transition-opacity hover:opacity-70" : undefined}
            >
              <HeroCard
                hue={style.hue}
                color={style.color}
                badge={`Parcours ${index + 1}`}
                title={program.title}
                description={program.goal}
                meta={meta}
                action={{
                  label,
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                      <path d="M4 2.5v9l7.5-4.5z" />
                    </svg>
                  ),
                }}
              />
            </Link>
          </div>
        );
      })}
    </div>
  );
}
